import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { AngaMark } from "@/components/brand/AngaMark";
import { Button } from "@/components/ui/button";
import { ApiError, authApi, persistSession } from "@/lib/api";
import { usePlatformConfig } from "@/lib/config";

export const Route = createFileRoute("/admin/login")({
  head: () => ({
    meta: [
      { title: "Admin login" },
      { name: "description", content: "Operator sign-in with email, password, and OTP." },
    ],
  }),
  component: AdminLoginPage,
});

function AdminLoginPage() {
  const navigate = useNavigate();
  const { platformName } = usePlatformConfig();
  const [step, setStep] = useState<"credentials" | "otp">("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [challengeToken, setChallengeToken] = useState("");
  const [devOtp, setDevOtp] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onCredentialsSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await authApi.adminLogin(email, password);
      setChallengeToken(result.challengeToken);
      setDevOtp(result.devOtp ?? null);
      if (result.devOtp) setOtp(result.devOtp);
      setStep("otp");
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : "Could not reach the backend. Start anga-backend or check VITE_API_URL.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }

  async function onOtpSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await authApi.adminVerifyOtp(email, otp, challengeToken);
      persistSession("admin", {
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
      });
      void navigate({ to: "/admin" });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : "OTP verification failed.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-4">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--primary-soft),_transparent_55%)]" />
      <div className="relative w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-sm md:p-8">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#064E3B] text-white">
            <AngaMark className="h-6 w-6" title={platformName} />
          </div>
          <div>
            <p className="font-display text-lg font-bold leading-none">{platformName}</p>
            <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
              Admin login
            </p>
          </div>
        </div>

        {step === "credentials" ? (
          <form className="space-y-4" onSubmit={onCredentialsSubmit}>
            <label className="block text-xs font-semibold">
              Email
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                autoComplete="username"
              />
            </label>
            <label className="block text-xs font-semibold">
              Password
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                autoComplete="current-password"
              />
            </label>

            {error && (
              <p className="rounded-md border border-destructive/25 bg-severe-soft px-3 py-2 text-xs text-severe">
                {error}
              </p>
            )}

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Checking…" : "Continue"}
            </Button>
          </form>
        ) : (
          <form className="space-y-4" onSubmit={onOtpSubmit}>
            <p className="text-sm text-muted-foreground">
              Enter the one-time code sent for{" "}
              <span className="font-semibold text-foreground">{email}</span>.
            </p>
            <label className="block text-xs font-semibold">
              OTP
              <input
                type="text"
                inputMode="numeric"
                required
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                className="mt-2 h-10 w-full rounded-md border border-input bg-background px-3 text-sm tracking-[0.2em]"
                autoComplete="one-time-code"
              />
            </label>

            {devOtp && (
              <p className="rounded-md border border-primary/20 bg-primary-soft px-3 py-2 text-xs text-foreground">
                Dev OTP: <span className="font-mono font-bold">{devOtp}</span>
              </p>
            )}

            {error && (
              <p className="rounded-md border border-destructive/25 bg-severe-soft px-3 py-2 text-xs text-severe">
                {error}
              </p>
            )}

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Verifying…" : "Verify and enter ops"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={() => {
                setStep("credentials");
                setError(null);
                setDevOtp(null);
                setOtp("");
              }}
            >
              Back
            </Button>
          </form>
        )}

        <p className="mt-4 text-center text-xs text-muted-foreground">
          <Link to="/" className="text-primary hover:underline">
            Back to portal
          </Link>
          {" · "}
          <Link to="/login" className="text-primary hover:underline">
            Resident login
          </Link>
        </p>
      </div>
    </div>
  );
}
